<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading :module-id="access.module_id" :profile-id="access.profile_id" :reference="access.view_reference" summary-only />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions"><TabHeaderActions /></div>
            <div class="list-view-heading__controls-end">
              <AccountingHelp view="accountingLedger" />
              <b-button variant="link" class="list-view-icon-btn" :class="{ 'list-view-icon-btn--active': filtersOpen }" v-b-tooltip.hover.bottom :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'" :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'" @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
            </div>
          </div>
        </section>
        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="width:170px;"><label class="filter-label">Empresa</label><b-form-select size="sm" v-model="filters.business_id" :options="arr.businesses"></b-form-select></div>
            <div class="list-view-filter" style="width:110px;"><label class="filter-label">Tipo</label><b-form-select size="sm" v-model="filters.declared" :options="arr.select.declared"></b-form-select></div>
            <div class="list-view-filter" style="width:150px;"><label class="filter-label">Desde</label><b-form-input type="date" size="sm" v-model="filters.date_start"></b-form-input></div>
            <div class="list-view-filter" style="width:150px;"><label class="filter-label">Hasta</label><b-form-input type="date" size="sm" v-model="filters.date_end"></b-form-input></div>
            <div class="list-view-filter" style="width:120px;"><label class="filter-label">Origen</label><b-form-select size="sm" v-model="filters.origin" :options="arr.select.origin"></b-form-select></div>
            <AccountingAccountsFilter style="width:240px;" v-model="selectedAccountIds" @plan-loaded="accountPlanFlat = $event" />
            <b-button variant="outline-dark" size="sm" @click="loadReport()" v-b-tooltip.hover title="Aplicar filtros" style="height:31px;width:31px;padding:0;flex-shrink:0;"><AppIcon name="filter" /></b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-card header-tag="header" footer-tag="footer">
        <b-row class="mt-3">
          <b-col md="4">
            <b-alert show variant="light" class="mb-2 accounting-ledger-total">
              <b>Debe</b>
              <div>{{ formatCurrency(summary.total_debit) }}</div>
            </b-alert>
          </b-col>
          <b-col md="4">
            <b-alert show variant="light" class="mb-2 accounting-ledger-total">
              <b>Haber</b>
              <div>{{ formatCurrency(summary.total_credit) }}</div>
            </b-alert>
          </b-col>
          <b-col md="4">
            <b-alert show :variant="summary.balance == 0 ? 'success' : 'warning'" class="mb-2 accounting-ledger-total">
              <b>Saldo</b>
              <div>{{ formatCurrency(summary.balance) }}</div>
            </b-alert>
          </b-col>
        </b-row>

        <b-row class="mt-2">
          <b-col>
            <b-table class="app-table mb-0"
                     sticky-header="60vh"
                     :hover="true"
                     :small="true"
                     :items="table.items"
                     :fields="table.fields"
                     v-if="table.items.length">
              <template v-slot:cell(account)="data">
                <span :style="{ paddingLeft: (data.item.level * 18) + 'px' }" :class="{ 'font-weight-bold': data.item.is_parent }">
                  <b>{{ data.item.accounting_accounts_code }}</b> - {{ data.item.accounting_accounts_name }}
                </span>
              </template>
              <template v-slot:cell(total_debit)="data">
                <b class="accounting-ledger-amount">{{ formatCurrency(data.item.total_debit) }}</b>
              </template>
              <template v-slot:cell(total_credit)="data">
                <b class="accounting-ledger-amount">{{ formatCurrency(data.item.total_credit) }}</b>
              </template>
              <template v-slot:cell(balance)="data">
                <b-badge :variant="data.item.balance == 0 ? 'success' : 'secondary'" class="accounting-ledger-amount-badge">
                  {{ formatCurrency(data.item.balance) }}
                </b-badge>
              </template>
              <template v-slot:cell(f_action)="data">
                <b-button size="sm" variant="outline-dark" @click="openMovements(data.item)" v-b-tooltip.hover title="Ver movimientos" v-if="data.item.movements.length">
                  <b-icon icon="eye" aria-hidden="true"></b-icon>
                </b-button>
              </template>
            </b-table>
            <EmptyState v-else-if="!loaded" icon="filter" title="Elegí los filtros" description="Tocá Aplicar filtros para generar el mayor." />
            <EmptyState v-else icon="account_balance" title="Sin movimientos" description="No hay movimientos con estos filtros." />
            <div class="app-table-footer" v-if="table.items.length">
              <span class="list-view-toolbar__count">{{ table.items.length }} cuentas</span>
              <export-excel class="list-view-link" :data="exportLedgerRows" worksheet="Mayor de Cuentas" :name="exportLedgerFileName">
                <AppIcon name="download" class="mr-1" /> Exportar
              </export-excel>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="back" />
            Volver
          </b-button>
        </div>
      </b-card>

      <b-modal v-model="modal.movements.active"
               size="xl"
               scrollable
               dialog-class="accounting-ledger-movements-dialog"
               :key="'movements_' + modal.movements._id"
               :id="modal.movements._id">
        <div slot="modal-header" minimize class="app-modal-header">
          <span class="app-modal-header__icon"><AppIcon name="list_alt" /></span>
          <h5 class="app-modal-header__title">{{ modal.movements.title }}</h5>
        </div>

        <AccountingEntrySource v-if="modal.movements.sourceId" class="accounting-ledger-source" :accounting-id="modal.movements.sourceId" @close="modal.movements.sourceId = 0" @loaded="revealSelectedMovement()" />
        <b-table class="app-table app-table--compact mb-0"
                 responsive="sm"
                 :hover="true"
                 :small="true"
                 :tbody-tr-class="movementRowClass"
                 :current-page="modal.movements.currentPage"
                 :per-page="modal.movements.perPage"
                 :items="modal.movements.items"
                 :fields="modal.movements.fields"
                 v-if="modal.movements.items.length">
          <template v-slot:cell(date)="data">
            {{ moment(data.item.date).format('DD/MM/YYYY') }}
          </template>
          <template v-slot:cell(origin)="data">
            <b-button v-if="data.item.source_type" size="sm" variant="link" class="list-view-link p-0" v-b-tooltip.hover title="Ver la operación de origen" @click="modal.movements.sourceId = data.item.accounting_id">
              {{ data.item.origin_movement.label }}
            </b-button>
            <b-badge v-else :variant="data.item.origin == 'manual' ? 'info' : 'secondary'">
              {{ data.item.origin_movement ? data.item.origin_movement.label : data.item.origin }}
            </b-badge>
          </template>
          <template v-slot:cell(debit)="data">
            <b>{{ formatCurrency(data.item.debit) }}</b>
          </template>
          <template v-slot:cell(credit)="data">
            <b>{{ formatCurrency(data.item.credit) }}</b>
          </template>
          <template v-slot:cell(balance)="data">
            <b>{{ formatCurrency(data.item.balance) }}</b>
          </template>
        </b-table>
        <EmptyState v-else icon="list_alt" title="Sin movimientos" description="Esta cuenta no tiene movimientos en el período." />

        <div slot="modal-footer" class="d-flex align-items-center">
          <b-pagination v-if="modal.movements.items.length > modal.movements.perPage" class="mb-0 mr-3" size="sm" pills="" :total-rows="modal.movements.items.length" :per-page="modal.movements.perPage" v-model="modal.movements.currentPage" />
          <b-button variant="outline-secondary" @click="modal.movements.active=false"><AppIcon name="close" class="mr-1" />Cerrar</b-button>
        </div>
      </b-modal>

    </b-col>
  </b-row>
</template>

<script>
  import AccountingHelp from './inc/AccountingHelp'
  import AccountingEntrySource from './inc/AccountingEntrySource'
  import AccountingAccountsFilter from './inc/AccountingAccountsFilter'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import StorageLocal from '@/handler/storageLocal'
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import moment from 'moment'

  // Crear un formateador por celda es caro: una cuenta puede tener miles de importes
  const CURRENCY_FORMAT = Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' })

  export default {
    components: {
      AccountingHelp,
      AccountingEntrySource,
      AccountingAccountsFilter,
      TabHeaderActions,
    },
    data: () => {
      return {
        filtersOpen: false,
        loaded: false,
        access: {
          module_id: Modules.CONTABILIDAD,
          profile_id: Profiles.PERSONAL,
          view_reference: 'accountingLedger',
          elements: {}
        },
        filters: {
          date_start: '',
          date_end: '',
          origin: '',
          accounting_accounts_ids: [],
          business_id: 0,
          declared: '',
        },
        summary: {
          total_debit: 0,
          total_credit: 0,
          balance: 0,
        },
        table: {
          items: [],
          rawItems: [],
          fields: [
            {key: 'account', label: 'Cuenta', class: 'align-middle'},
            {key: 'total_debit', label: 'Debe', class: 'align-middle text-right'},
            {key: 'total_credit', label: 'Haber', class: 'align-middle text-right'},
            {key: 'balance', label: 'Saldo', class: 'align-middle text-right'},
            {key: 'f_action', label: '', class: 'align-middle text-right'},
          ],
        },
        arr: {
          businesses: [],
          select: {
            origin: [
              { value: '', text: 'Todos' },
              { value: 'automatic', text: 'Auto' },
              { value: 'manual', text: 'Manual' },
            ],
            declared: [
              { value: '', text: 'Todos' },
              { value: '1', text: 'D' },
              { value: '0', text: 'ND' },
            ],
          }
        },
        accountPlanFlat: [],
        selectedAccountIds: null,
        modal: {
          movements: {
            sourceId: 0,
            currentPage: 1,
            perPage: 100,
            active: false,
            title: '',
            _id: '',
            items: [],
            fields: [
              {key: 'date', label: 'Fecha', class: 'align-middle'},
              {key: 'reference', label: 'Referencia', class: 'align-middle accounting-ledger-reference'},
              {key: 'origin', label: 'Origen', class: 'align-middle text-center'},
              {key: 'debit', label: 'Debe', class: 'align-middle text-right'},
              {key: 'credit', label: 'Haber', class: 'align-middle text-right'},
              {key: 'balance', label: 'Saldo', class: 'align-middle text-right'},
            ],
          },
        }
      }
    },
    created () {
      Helper.hasAccessView(this.access)
    },
    watch: {
      filtersOpen(value) {
        StorageLocal.setValue('filters_open_accounting_ledger', value ? 'true' : 'false')
      },
    },
    mounted() {
      this.filtersOpen = StorageLocal.getValue('filters_open_accounting_ledger') == 'true'
      this.filterLoad()
      this.loadBusinesses()
    },
    computed: {
      exportLedgerFileName() {
        let start = this.filters.date_start || 'desde'
        let end = this.filters.date_end || 'hasta'
        return 'mayor-cuentas-' + start + '-' + end + '.xls'
      },
      exportLedgerRows() {
        const fmt = v => parseFloat(v || 0).toFixed(2).replace('.', ',')
        return this.table.items.map(item => {
          return {
            Cuenta: this.getIndentedAccountName(item),
            Debe:  fmt(item.total_debit),
            Haber: fmt(item.total_credit),
            Saldo: fmt(item.balance),
          }
        })
      },
    },
    methods: {
      moment,
      filterLoad() {
        this.filters.date_start = moment().startOf('month').format('YYYY-MM-DD')
        this.filters.date_end = moment().format('YYYY-MM-DD')
      },
      loadBusinesses() {
        serviceAPI.obtenerEmpresas()
        .then(response => {
          const businesses = response.data || []
          this.arr.businesses = businesses.map(b => ({ value: b.id, text: b.name }))
          if(businesses.length) {
            this.filters.business_id = businesses[0].id
          }
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      formatCurrency(value) {
        return CURRENCY_FORMAT.format(value || 0)
      },
      getIndentedAccountName(item) {
        let prefix = ''
        for(let i = 0; i < (item.level || 0); i++) {
          prefix = prefix + '   '
        }

        return prefix + item.accounting_accounts_code + ' - ' + item.accounting_accounts_name
      },
      loadReport() {
        let loader = this.$loading.show()
        const selectedIds = this.selectedAccountIds || []
        this.filters.accounting_accounts_ids = selectedIds.length ? selectedIds : [-1]

        serviceAPI.mayorCuentas(this.filters)
        .then((response) => {
          loader.hide()
          this.summary = response.data.summary || {
            total_debit: 0,
            total_credit: 0,
            balance: 0,
          }
          // Los movimientos solo se leen: congelados, Vue no los recorre para hacerlos reactivos
          const accounts = response.data.accounts || []
          accounts.forEach(account => Object.freeze(account.movements))
          this.table.rawItems = Object.freeze(accounts)
          this.table.items = this.buildTreeRows(accounts)
          this.loaded = true
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
      buildTreeRows(rawItems) {
        let rawByAccount = {}
        rawItems.forEach(item => {
          rawByAccount[item.accounting_accounts_id] = item
        })

        if(!this.accountPlanFlat.length) {
          return rawItems
        }

        let rows = []
        this.accountPlanFlat.forEach(account => {
          if((this.selectedAccountIds || []).indexOf(account.id) < 0) {
            return
          }

          let row = rawByAccount[account.id] || {
            accounting_accounts_id: account.id,
            accounting_accounts_code: account.code,
            accounting_accounts_name: account.name,
            total_debit: 0,
            total_credit: 0,
            balance: 0,
            movements: [],
          }

          let totals = this.calculateAccountTotals(account, rawByAccount)
          row.total_debit = totals.total_debit
          row.total_credit = totals.total_credit
          row.balance = totals.balance
          row.level = account.level
          row.is_parent = account.children_ids.length > 0

          if(row.movements.length || row.is_parent) {
            rows.push(row)
          }
        })

        return rows
      },
      calculateAccountTotals(account, rawByAccount) {
        let totalDebit = 0
        let totalCredit = 0
        let ids = [account.id].concat(account.children_ids)

        ids.forEach(id => {
          if(rawByAccount[id]) {
            totalDebit = totalDebit + parseFloat(rawByAccount[id].total_debit || 0)
            totalCredit = totalCredit + parseFloat(rawByAccount[id].total_credit || 0)
          }
        })

        return {
          total_debit: parseFloat(totalDebit.toFixed(2)),
          total_credit: parseFloat(totalCredit.toFixed(2)),
          balance: parseFloat((totalDebit - totalCredit).toFixed(2)),
        }
      },
      movementRowClass(item, type) {
        if(type !== 'row' || !this.modal.movements.sourceId) return ''
        return item.accounting_id == this.modal.movements.sourceId ? 'accounting-ledger-movement--selected' : ''
      },
      // El panel ocupa lugar arriba: la fila elegida tiene que quedar a la vista, por debajo de él.
      revealSelectedMovement() {
        const row = document.querySelector('.accounting-ledger-movement--selected')
        const body = row ? row.closest('.modal-body') : null
        if(!body) return

        const panel = body.querySelector('.accounting-ledger-source')
        const bodyRect = body.getBoundingClientRect()
        const rowRect = row.getBoundingClientRect()
        const top = Math.max(bodyRect.top, panel ? panel.getBoundingClientRect().bottom : 0) + 8
        if(rowRect.top < top) {
          body.scrollTop -= top - rowRect.top
        } else if(rowRect.bottom > bodyRect.bottom - 8) {
          body.scrollTop += rowRect.bottom - bodyRect.bottom + 8
        }
      },
      openMovements(item) {
        this.modal.movements.sourceId = 0
        this.modal.movements.currentPage = 1
        this.modal.movements._id = Math.random().toString(36).slice(2, 8)
        this.modal.movements.title = item.accounting_accounts_code + ' - ' + item.accounting_accounts_name
        this.modal.movements.items = item.movements || []
        this.modal.movements.active = true
      },
    }
  }
</script>

<style>
  @media (min-width: 1200px) {
    .accounting-ledger-movements-dialog { max-width: min(96vw, 1560px); }
  }
  /* la referencia puede ser larga: corta en renglones para que los importes queden dentro del modal */
  .app-table td.accounting-ledger-reference { white-space: normal; }
  /* el origen queda a la vista aunque se haya abierto desde una fila de abajo */
  @media (min-width: 768px) {
    .accounting-ledger-source { position: sticky; top: -1rem; z-index: 3; }
  }
  .accounting-ledger-movement--selected td { background-color: var(--app-color-primary-soft) !important; }
</style>

<style scoped>
  .accounting-ledger-total div {
    font-size: 20px;
    font-weight: 700;
    line-height: 1.2;
  }

  .accounting-ledger-amount {
    font-size: 15px;
  }

  .accounting-ledger-amount-badge {
    font-size: 14px;
    padding: 6px 8px;
  }

  .filter-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 2px;
    color: #495057;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
